import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { formatBytes } from '@utils/storageManagement/diagnostics';

import {
  type EhrUploadFile,
  useLazyListAdminEhrUploadFilesQuery,
  useLazyListEhrUploadFilesQuery,
} from '../../store/ehrApi';
import { wf } from '../shell/tokens';

/** Read stored names from S3 pages; never infer saved files from a local selection. */
export function StoredEhrFiles({
  uploadId,
  organizationId,
  staff = false,
  revision,
}: {
  uploadId: string;
  organizationId: string;
  staff?: boolean;
  revision?: string | null;
}) {
  const [customerRead] = useLazyListEhrUploadFilesQuery();
  const [adminRead] = useLazyListAdminEhrUploadFilesQuery();
  const [files, setFiles] = useState<EhrUploadFile[]>([]);
  const [cursor, setCursor] = useState<string | null>();
  const [busy, setBusy] = useState(true);
  const [failed, setFailed] = useState(false);
  const request = useRef<
    ReturnType<typeof customerRead> | ReturnType<typeof adminRead> | undefined
  >(undefined);
  const generation = useRef(0);
  const read = useCallback(
    async (nextCursor?: string) => {
      const current = generation.current;
      setBusy(true);
      setFailed(false);
      for (let attempt = 0; attempt < 2; attempt++) {
        const pending = staff
          ? adminRead({ organizationId, uploadId, cursor: nextCursor }, false)
          : customerRead({ uploadId, cursor: nextCursor }, false);
        request.current = pending;
        const result = await pending;
        pending.unsubscribe();
        if (current !== generation.current) return;
        if (result.requestId !== pending.requestId) continue;
        if (result.data) {
          const saved = result.data;
          setFiles(previous => (nextCursor ? [...previous, ...saved.files] : saved.files));
          setCursor(saved.next_cursor);
        } else setFailed(true);
        setBusy(false);
        return;
      }
      setFailed(true);
      setBusy(false);
    },
    [adminRead, customerRead, organizationId, staff, uploadId],
  );
  useEffect(() => {
    generation.current += 1;
    setFiles([]);
    setCursor(undefined);
    void read();
    return () => {
      generation.current += 1;
      request.current?.abort();
      request.current?.unsubscribe();
    };
  }, [read, revision]);
  return (
    <Box sx={{ my: 1 }}>
      {busy && <LinearProgress aria-label="Loading saved EHR files" />}
      {failed && (
        <Alert severity="error" action={<Button onClick={() => void read()}>Retry</Button>}>
          Could not load saved files.
        </Alert>
      )}
      {staff ? (
        <Box component="ul" sx={{ pl: 2.5 }}>
          {files.map(file => (
            <Typography
              component="li"
              variant="body2"
              key={file.filename}
              sx={{ overflowWrap: 'anywhere' }}
            >
              {file.filename} · {file.size_bytes.toLocaleString()} bytes
            </Typography>
          ))}
        </Box>
      ) : (
        <Stack component="ul" sx={{ listStyle: 'none', margin: 0, padding: 0, gap: '3px' }}>
          {files.map(file => (
            <Typography
              component="li"
              key={file.filename}
              sx={{
                fontSize: '13px',
                lineHeight: 1.45,
                color: '#52526a',
                overflowWrap: 'anywhere',
              }}
            >
              {file.filename}
              <Box component="span" sx={{ color: wf.meta }}>
                {' '}
                · {formatBytes(file.size_bytes)}
              </Box>
            </Typography>
          ))}
        </Stack>
      )}
      {!busy && !failed && !files.length && (
        <Typography variant="body2" color="text.secondary">
          No files saved in this upload yet.
        </Typography>
      )}
      {cursor && (
        <Button disabled={busy} onClick={() => void read(cursor)}>
          Show more files
        </Button>
      )}
    </Box>
  );
}
